import React, { useState } from 'react';
import { useSermons } from '../../hooks/useSermons';
import { useServiceAssets } from '../../hooks/useServiceAssets';
import {
  Music, BookOpen, FileText, Loader2, AlertCircle,
  Download, Mail, Archive, Trash2, RotateCcw,
  ChevronDown, ChevronUp, X, Copy, Check,
  ArrowUp, ArrowDown, Filter, Layers
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { createStyledPDF, addSection, cleanAIArray } from '../../lib/pdf';
import { supabase } from '../../lib/supabase';
import { logger } from '../../lib/logger';
import { parseISO } from 'date-fns';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SongSuggestion {
  title: string;
  artist: string;
  key: string;
  relevance: number;
  role: string;
  reason?: string;
}

interface HymnSuggestion {
  title: string;
  author?: string;
  relevance: number;
  reason?: string;
}

interface ReadingSuggestion {
  reference: string;
  theme: string;
  relevance: number;
}

interface SetlistItem {
  id: string;
  title: string;
  artist: string;
  key: string;
  role: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const ROLES = ['opener', 'worship', 'response', 'closing', 'communion', 'offering', 'reading'];

const ROLE_STYLES: Record<string, string> = {
  opener:    'bg-gold/15 text-gold border-gold/20',
  worship:   'bg-electric/15 text-electric border-electric/20',
  response:  'bg-fiery/15 text-fiery border-fiery/20',
  closing:   'bg-slate/15 text-slate border-slate/20',
  communion: 'bg-crimson/15 text-crimson border-crimson/20',
  offering:  'bg-emerald/15 text-emerald border-emerald/20',
  reading:   'bg-fiery/15 text-fiery border-fiery/20',
};

const COMMON_KEYS = ['G', 'D', 'A', 'E', 'C', 'F', 'Bb', 'Eb'];

// ── Parsers ───────────────────────────────────────────────────────────────────

function parseSong(s: string): SongSuggestion {
  try {
    const p = JSON.parse(s);
    return {
      title: p.title || s,
      artist: p.artist || '',
      key: p.key || '',
      relevance: Number(p.relevance) || 85,
      role: p.role || 'worship',
      reason: p.reason,
    };
  } catch {
    const dash = s.lastIndexOf(' - ');
    if (dash !== -1) return { title: s.slice(0, dash), artist: s.slice(dash + 3), key: '', relevance: 85, role: 'worship' };
    return { title: s, artist: '', key: '', relevance: 85, role: 'worship' };
  }
}

function parseHymn(s: string): HymnSuggestion {
  try {
    const p = JSON.parse(s);
    return { title: p.title || s, author: p.author, relevance: Number(p.relevance) || 80, reason: p.reason };
  } catch {
    return { title: s, relevance: 80 };
  }
}

function parseReading(s: string): ReadingSuggestion {
  try {
    const p = JSON.parse(s);
    return { reference: p.reference || s, theme: p.theme || '', relevance: Number(p.relevance) || 85 };
  } catch {
    return { reference: s, theme: '', relevance: 85 };
  }
}

function parseTextFallback(text: string): { songs: string[]; hymns: string[]; readings: string[] } {
  const songs: string[] = [];
  const hymns: string[] = [];
  const readings: string[] = [];
  let current: 'songs' | 'hymns' | 'readings' | null = null;

  text.split(/\n/).forEach(line => {
    const t = line.trim();
    if (/^songs:/i.test(t)) { current = 'songs'; return; }
    if (/^hymns:/i.test(t)) { current = 'hymns'; return; }
    if (/^readings:/i.test(t)) { current = 'readings'; return; }
    if (current && t && (t.startsWith('-') || t.startsWith('•'))) {
      const item = t.replace(/^[-•]\s*/, '');
      if (item) ({ songs, hymns, readings }[current] as string[]).push(item);
    }
  });
  return { songs, hymns, readings };
}

function getKeyNote(items: SetlistItem[]): string | null {
  const keys = items.map(i => i.key).filter(Boolean);
  if (keys.length < 2) return null;
  const unique = [...new Set(keys)];
  if (unique.length === 1) return `All songs in key of ${unique[0]} — smooth transitions`;
  return `Key flow: ${items.filter(i => i.key).map(i => i.key).join(' → ')}`;
}

// ── Asset Card ────────────────────────────────────────────────────────────────

interface AssetCardProps {
  asset: {
    id: string;
    sermon_id: string | null;
    songs: string[];
    hymns: string[];
    readings: string[];
    archived: boolean;
    created_at: string;
  };
  sermon: { title: string; date?: string; scripture?: string } | undefined;
  onDownload: () => void;
  onShare: () => void;
  onArchive: () => void;
  onDelete: () => void;
}

function AssetCard({ asset, sermon, onDownload, onShare, onArchive, onDelete }: AssetCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [setlist, setSetlist] = useState<SetlistItem[]>([]);
  const [keyFilter, setKeyFilter] = useState('');
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [activeTab, setActiveTab] = useState<'songs' | 'hymns' | 'readings'>('songs');

  const songs = asset.songs.map(parseSong);
  const hymns = asset.hymns.map(parseHymn);
  const readings = asset.readings.map(parseReading);

  // Detect legacy plain-text format (pre-JSON): if no entry can be JSON-parsed, it's old data
  const isLegacyData = asset.songs.length > 0 && (() => {
    try { JSON.parse(asset.songs[0]); return false; } catch { return true; }
  })();

  const filteredSongs = keyFilter
    ? songs.filter(s => s.key && s.key.toUpperCase() === keyFilter.toUpperCase())
    : songs;

  const inSetlist = (title: string) => setlist.some(i => i.title === title);

  const addToSetlist = (item: { title: string; artist?: string; key?: string; role?: string }) => {
    if (inSetlist(item.title)) return;
    setSetlist(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      title: item.title,
      artist: item.artist || '',
      key: item.key || '',
      role: item.role || 'worship',
    }]);
  };

  const removeFromSetlist = (id: string) => setSetlist(prev => prev.filter(i => i.id !== id));

  const moveItem = (id: string, dir: 'up' | 'down') => {
    setSetlist(prev => {
      const idx = prev.findIndex(i => i.id === id);
      if (dir === 'up' && idx === 0) return prev;
      if (dir === 'down' && idx === prev.length - 1) return prev;
      const next = [...prev];
      const swap = dir === 'up' ? idx - 1 : idx + 1;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  };

  const changeRole = (id: string, role: string) => {
    setSetlist(prev => prev.map(i => i.id === id ? { ...i, role } : i));
    setEditingRole(null);
  };

  const copySetlist = () => {
    if (!setlist.length) return;
    const lines = setlist.map((item, i) =>
      `${i + 1}. ${item.title}${item.artist ? ` — ${item.artist}` : ''}${item.key ? ` (Key: ${item.key})` : ''} [${item.role}]`
    );
    const note = getKeyNote(setlist);
    if (note) lines.push('', note);
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const downloadSetlistPDF = () => {
    if (!setlist.length || !sermon) return;

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = 210;
    const pageH = 297;
    const ml = 20;
    const mr = 20;

    const C = {
      midnight: [28, 43, 57] as [number, number, number],
      slate:    [79, 93, 117] as [number, number, number],
      muted:    [155, 163, 175] as [number, number, number],
      divider:  [220, 212, 200] as [number, number, number],
      sand:     [242, 232, 213] as [number, number, number],
      sandBorder:[205, 195, 180] as [number, number, number],
      emerald:  [45, 127, 94] as [number, number, number],
      white:    [255, 255, 255] as [number, number, number],
    };

    const ROLE_CLRS: Record<string, [number, number, number]> = {
      opener:    [217, 164, 65],
      worship:   [40, 116, 166],
      response:  [232, 106, 51],
      closing:   [79, 93, 117],
      communion: [178, 42, 47],
      offering:  [45, 127, 94],
      reading:   [232, 106, 51],
    };

    let y = 18;

    // Branding (top-right)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.muted);
    doc.text('SermonBuild', pageW - mr, y, { align: 'right' });

    // Title block
    y = 30;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(...C.midnight);
    doc.text('SERVICE ORDER', pageW / 2, y, { align: 'center' });
    y += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(...C.slate);
    const titleLines = doc.splitTextToSize(sermon.title, pageW - ml - mr - 20);
    doc.text(titleLines, pageW / 2, y, { align: 'center' });
    y += (titleLines.length * 6) + 2;

    if (sermon.date) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(...C.muted);
      const dateStr = parseISO(sermon.date).toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      });
      doc.text(dateStr, pageW / 2, y, { align: 'center' });
      y += 6;
    }

    y += 5;
    doc.setDrawColor(...C.divider);
    doc.setLineWidth(0.4);
    doc.line(ml, y, pageW - mr, y);
    y += 9;

    // Setlist items
    setlist.forEach((item, i) => {
      if (y > pageH - 35) { doc.addPage(); y = 22; }

      const rc = ROLE_CLRS[item.role] || C.slate;
      const circleX = ml + 4.5;
      const circleY = y + 3;

      // Numbered circle
      doc.setFillColor(rc[0], rc[1], rc[2]);
      doc.circle(circleX, circleY, 4, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...C.white);
      doc.text(String(i + 1), circleX, circleY + 1.1, { align: 'center' });

      const textX = ml + 12;

      // Title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...C.midnight);
      // Truncate title to leave room for right-side pills (~90mm)
      const titleMaxW = pageW - mr - textX - 42;
      const itemTitleLines = doc.splitTextToSize(item.title, titleMaxW);
      doc.text(itemTitleLines[0], textX, y + 2);

      // Right-side pills (positioned from right margin)
      let rightX = pageW - mr;

      // Role pill
      const roleLabel = item.role.toUpperCase();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      const roleLabelW = doc.getTextWidth(roleLabel);
      const pillPad = 2.5;
      const pillW = roleLabelW + pillPad * 2;
      const pillH = 4.5;
      const pillY = y;
      const pillX = rightX - pillW;
      doc.setFillColor(rc[0], rc[1], rc[2]);
      doc.roundedRect(pillX, pillY, pillW, pillH, 1, 1, 'F');
      doc.setTextColor(...C.white);
      doc.text(roleLabel, pillX + pillPad, pillY + 3.2);
      rightX = pillX - 2.5;

      // Key pill
      if (item.key) {
        const keyText = `Key: ${item.key}`;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        const keyLabelW = doc.getTextWidth(keyText);
        const keyW = keyLabelW + 5;
        const keyX = rightX - keyW;
        doc.setFillColor(...C.sand);
        doc.setDrawColor(...C.sandBorder);
        doc.setLineWidth(0.2);
        doc.roundedRect(keyX, pillY, keyW, pillH, 1, 1, 'FD');
        doc.setTextColor(...C.slate);
        doc.text(keyText, keyX + 2.5, pillY + 3.2);
      }

      y += 7;

      // Artist / author / theme
      if (item.artist) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...C.muted);
        doc.text(item.artist, textX, y);
        y += 5;
      }

      // Divider between items
      if (i < setlist.length - 1) {
        y += 2;
        doc.setDrawColor(...C.divider);
        doc.setLineWidth(0.15);
        doc.line(textX, y, pageW - mr, y);
        y += 5;
      } else {
        y += 4;
      }
    });

    // Key note
    const note = getKeyNote(setlist);
    if (note) {
      y += 5;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(...C.emerald);
      doc.text(note, pageW / 2, y, { align: 'center' });
    }

    // Footer on every page
    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...C.muted);
      doc.text('Created with SermonBuild', ml, pageH - 8);
      if (totalPages > 1) {
        doc.text(`${p} / ${totalPages}`, pageW - mr, pageH - 8, { align: 'right' });
      }
    }

    const slug = sermon.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    doc.save(`setlist-${slug}.pdf`);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    try {
      const song: SongSuggestion = JSON.parse(e.dataTransfer.getData('application/json'));
      addToSetlist(song);
    } catch { /* ignore */ }
  };

  const keyNote = getKeyNote(setlist);

  if (!sermon) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-sand/30 overflow-hidden">
      {/* Card Header */}
      <div
        className="p-5 cursor-pointer hover:bg-sand/5 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-midnight truncate">{sermon.title}</h3>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate/70">
              <span>{new Date(asset.created_at).toLocaleDateString()}</span>
              {songs.length > 0 && <span>{songs.length} songs</span>}
              {hymns.length > 0 && <span>{hymns.length} hymns</span>}
              {readings.length > 0 && <span>{readings.length} readings</span>}
            </div>
          </div>

          <div className="flex items-center gap-1 ml-4">
            <button
              onClick={e => { e.stopPropagation(); onDownload(); }}
              className="p-2 text-slate/60 hover:text-electric rounded-lg transition-colors"
              title="Download PDF"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={e => { e.stopPropagation(); onShare(); }}
              className="p-2 text-slate/60 hover:text-electric rounded-lg transition-colors"
              title="Share via Email"
            >
              <Mail className="h-4 w-4" />
            </button>
            <button
              onClick={e => { e.stopPropagation(); onArchive(); }}
              className="p-2 text-slate/60 hover:text-electric rounded-lg transition-colors"
              title={asset.archived ? 'Unarchive' : 'Archive'}
            >
              {asset.archived ? <RotateCcw className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
            </button>
            <button
              onClick={e => {
                e.stopPropagation();
                if (window.confirm('Delete these suggestions?')) onDelete();
              }}
              className="p-2 text-slate/60 hover:text-crimson rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <div className="w-px h-5 bg-sand/40 mx-1" />
            {expanded
              ? <ChevronUp className="h-4 w-4 text-slate/50" />
              : <ChevronDown className="h-4 w-4 text-slate/50" />
            }
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-sand/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-sand/20">

            {/* Left: Suggestions */}
            <div className="lg:col-span-2 p-5 space-y-5">

              {/* Legacy data warning */}
              {isLegacyData && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-gold/10 border border-gold/20 text-xs text-gold">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>This set was generated with an older format — relevance scores and key data aren't available. Delete and regenerate to unlock them.</span>
                </div>
              )}

              {/* Tab Nav */}
              <div className="flex gap-1 border-b border-sand/20 pb-0">
                {(['songs', 'hymns', 'readings'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg capitalize transition-colors ${
                      activeTab === tab
                        ? 'bg-electric/10 text-electric border-b-2 border-electric'
                        : 'text-slate/60 hover:text-slate'
                    }`}
                  >
                    {tab === 'songs' && <Music className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />}
                    {tab === 'hymns' && <BookOpen className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />}
                    {tab === 'readings' && <FileText className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />}
                    {tab}
                  </button>
                ))}
              </div>

              {/* Songs Tab */}
              {activeTab === 'songs' && (
                <div className="space-y-3">
                  {/* Key Filter */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-slate/60 flex items-center gap-1">
                      <Filter className="h-3 w-3" /> Key:
                    </span>
                    <button
                      onClick={() => setKeyFilter('')}
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                        !keyFilter ? 'bg-midnight text-white' : 'bg-sand/50 text-slate hover:bg-sand'
                      }`}
                    >
                      All
                    </button>
                    {COMMON_KEYS.map(k => (
                      <button
                        key={k}
                        onClick={() => setKeyFilter(keyFilter === k ? '' : k)}
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                          keyFilter === k ? 'bg-electric text-white' : 'bg-sand/50 text-slate hover:bg-sand'
                        }`}
                      >
                        {k}
                      </button>
                    ))}
                  </div>

                  <p className="text-xs text-slate/50 text-right">Drag to setlist or click + Add</p>

                  {filteredSongs.length === 0 ? (
                    <p className="text-sm text-slate/60 py-4 text-center">
                      {keyFilter && isLegacyData
                        ? 'Key data not available — regenerate this set to enable key filtering'
                        : keyFilter
                        ? `No songs suggested in key of ${keyFilter}`
                        : 'No songs suggested'}
                    </p>
                  ) : (
                    filteredSongs.map((song, i) => {
                      const added = inSetlist(song.title);
                      return (
                        <div
                          key={i}
                          draggable={!added}
                          onDragStart={e => e.dataTransfer.setData('application/json', JSON.stringify(song))}
                          className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                            added
                              ? 'bg-emerald/5 border-emerald/20 opacity-60'
                              : 'bg-white border-sand/30 hover:border-electric/30 hover:shadow-sm cursor-grab active:cursor-grabbing'
                          }`}
                        >
                          {/* Icon */}
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-sand/60 flex items-center justify-center">
                            <Music className="h-5 w-5 text-gold" />
                          </div>

                          {/* Title + Artist */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-midnight truncate">{song.title}</p>
                            {song.artist && (
                              <p className="text-xs text-slate/70 truncate">{song.artist}</p>
                            )}
                            {song.reason && (
                              <p className="text-xs text-slate/50 mt-0.5 truncate">{song.reason}</p>
                            )}
                          </div>

                          {/* Key + Relevance */}
                          <div className="flex-shrink-0 flex flex-col items-end gap-1.5 min-w-[90px]">
                            {song.key && (
                              <span className="text-xs font-medium bg-sand px-2 py-0.5 rounded-md text-slate">
                                Key: {song.key}
                              </span>
                            )}
                            {!isLegacyData && (
                              <div className="flex items-center gap-1.5">
                                <div className="w-16 h-1.5 bg-sand rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-emerald rounded-full transition-all"
                                    style={{ width: `${song.relevance}%` }}
                                  />
                                </div>
                                <span className="text-xs font-semibold text-emerald w-8 text-right">
                                  {song.relevance}%
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Add Button */}
                          <button
                            onClick={() => addToSetlist(song)}
                            disabled={added}
                            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors whitespace-nowrap ${
                              added
                                ? 'bg-emerald/10 text-emerald border-emerald/30 cursor-default'
                                : 'bg-electric text-white border-electric hover:bg-electric/90'
                            }`}
                          >
                            {added ? '✓ Added' : '+ Add'}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Hymns Tab */}
              {activeTab === 'hymns' && (
                <div className="space-y-3">
                  {hymns.length === 0 ? (
                    <p className="text-sm text-slate/60 py-4 text-center">No hymns suggested</p>
                  ) : (
                    hymns.map((hymn, i) => {
                      const added = inSetlist(hymn.title);
                      return (
                        <div
                          key={i}
                          draggable={!added}
                          onDragStart={e => e.dataTransfer.setData('application/json', JSON.stringify({ title: hymn.title, artist: hymn.author || '', key: '', role: 'worship' }))}
                          className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${added ? 'bg-emerald/5 border-emerald/20 opacity-60' : 'bg-white border-sand/30 hover:border-electric/30 hover:shadow-sm cursor-grab active:cursor-grabbing'}`}
                        >
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-sand/60 flex items-center justify-center">
                            <BookOpen className="h-5 w-5 text-electric" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-midnight">{hymn.title}</p>
                            {hymn.author && <p className="text-xs text-slate/70">{hymn.author}</p>}
                            {hymn.reason && <p className="text-xs text-slate/50 mt-0.5">{hymn.reason}</p>}
                          </div>
                          {!isLegacyData && (
                            <div className="flex-shrink-0 flex items-center gap-1.5">
                              <div className="w-16 h-1.5 bg-sand rounded-full overflow-hidden">
                                <div className="h-full bg-electric/70 rounded-full" style={{ width: `${hymn.relevance}%` }} />
                              </div>
                              <span className="text-xs font-semibold text-electric w-8 text-right">{hymn.relevance}%</span>
                            </div>
                          )}
                          <button
                            onClick={() => addToSetlist({ title: hymn.title, artist: hymn.author, key: '', role: 'worship' })}
                            disabled={added}
                            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors whitespace-nowrap ${added ? 'bg-emerald/10 text-emerald border-emerald/30 cursor-default' : 'bg-electric text-white border-electric hover:bg-electric/90'}`}
                          >
                            {added ? '✓ Added' : '+ Add'}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Readings Tab */}
              {activeTab === 'readings' && (
                <div className="space-y-3">
                  {readings.length === 0 ? (
                    <p className="text-sm text-slate/60 py-4 text-center">No readings suggested</p>
                  ) : (
                    readings.map((reading, i) => {
                      const added = inSetlist(reading.reference);
                      return (
                        <div
                          key={i}
                          draggable={!added}
                          onDragStart={e => e.dataTransfer.setData('application/json', JSON.stringify({ title: reading.reference, artist: reading.theme || '', key: '', role: 'reading' }))}
                          className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${added ? 'bg-emerald/5 border-emerald/20 opacity-60' : 'bg-white border-sand/30 hover:border-fiery/30 hover:shadow-sm cursor-grab active:cursor-grabbing'}`}
                        >
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-sand/60 flex items-center justify-center mt-0.5">
                            <FileText className="h-5 w-5 text-fiery" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-midnight">{reading.reference}</p>
                            {reading.theme && <p className="text-xs text-slate/70 mt-0.5 leading-relaxed">{reading.theme}</p>}
                          </div>
                          {!isLegacyData && (
                            <div className="flex-shrink-0 flex items-center gap-1.5 mt-1">
                              <div className="w-12 h-1.5 bg-sand rounded-full overflow-hidden">
                                <div className="h-full bg-fiery/70 rounded-full" style={{ width: `${reading.relevance}%` }} />
                              </div>
                              <span className="text-xs font-semibold text-fiery w-8 text-right">{reading.relevance}%</span>
                            </div>
                          )}
                          <button
                            onClick={() => addToSetlist({ title: reading.reference, artist: reading.theme, key: '', role: 'reading' })}
                            disabled={added}
                            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors whitespace-nowrap ${added ? 'bg-emerald/10 text-emerald border-emerald/30 cursor-default' : 'bg-fiery text-white border-fiery hover:bg-fiery/90'}`}
                          >
                            {added ? '✓ Added' : '+ Add'}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Right: Setlist Builder */}
            <div className="lg:col-span-1 p-5 bg-sand/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-slate/60" />
                  <span className="text-xs font-bold tracking-widest text-slate/60 uppercase">Your Setlist</span>
                </div>
                {setlist.length > 0 && (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={copySetlist}
                      className="flex items-center gap-1.5 text-xs text-slate/60 hover:text-slate transition-colors"
                    >
                      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      onClick={downloadSetlistPDF}
                      className="flex items-center gap-1.5 text-xs text-electric hover:text-electric/80 transition-colors font-medium"
                    >
                      <Download className="h-3.5 w-3.5" />
                      PDF
                    </button>
                  </div>
                )}
              </div>

              {/* Drop Zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
                className={`min-h-[120px] rounded-xl transition-all ${
                  isDragOver
                    ? 'bg-electric/5 border-2 border-dashed border-electric'
                    : setlist.length === 0
                    ? 'border-2 border-dashed border-sand/50 bg-white/50'
                    : ''
                }`}
              >
                {setlist.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-28 text-center px-4">
                    <Music className="h-8 w-8 text-sand mb-2" />
                    <p className="text-xs text-slate/50">Drag songs here or click + to build your setlist</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {setlist.map((item, i) => (
                      <div key={item.id} className="bg-white rounded-xl border border-sand/30 p-3 shadow-sm">
                        <div className="flex items-start gap-2">
                          {/* Number */}
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald flex items-center justify-center text-white text-xs font-bold mt-0.5">
                            {i + 1}
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-midnight leading-tight truncate">{item.title}</p>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              {item.key && (
                                <span className="text-xs bg-sand px-1.5 py-0.5 rounded text-slate font-medium">
                                  {item.key}
                                </span>
                              )}
                              {/* Role badge (click to edit) */}
                              {editingRole === item.id ? (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {ROLES.map(r => (
                                    <button
                                      key={r}
                                      onClick={() => changeRole(item.id, r)}
                                      className={`px-2 py-0.5 rounded-full text-xs font-medium border capitalize transition-colors ${
                                        ROLE_STYLES[r] || 'bg-sand/30 text-slate border-sand'
                                      }`}
                                    >
                                      {r}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <button
                                  onClick={() => setEditingRole(item.id)}
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium border capitalize transition-opacity hover:opacity-80 ${
                                    ROLE_STYLES[item.role] || 'bg-sand/30 text-slate border-sand'
                                  }`}
                                  title="Click to change role"
                                >
                                  {item.role}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Controls */}
                          <div className="flex-shrink-0 flex flex-col gap-0.5">
                            <button
                              onClick={() => moveItem(item.id, 'up')}
                              disabled={i === 0}
                              className="p-0.5 text-slate/40 hover:text-slate disabled:opacity-20 transition-colors"
                            >
                              <ArrowUp className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => moveItem(item.id, 'down')}
                              disabled={i === setlist.length - 1}
                              className="p-0.5 text-slate/40 hover:text-slate disabled:opacity-20 transition-colors"
                            >
                              <ArrowDown className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => removeFromSetlist(item.id)}
                              className="p-0.5 text-slate/40 hover:text-crimson transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Key Note */}
              {keyNote && (
                <p className="mt-3 text-xs text-emerald font-medium text-center">{keyNote}</p>
              )}

              {/* Setlist Stats */}
              {setlist.length > 0 && (
                <div className="mt-3 pt-3 border-t border-sand/30 flex justify-between text-xs text-slate/60">
                  <span>{setlist.length} song{setlist.length !== 1 ? 's' : ''}</span>
                  <button
                    onClick={() => setSetlist([])}
                    className="text-crimson/60 hover:text-crimson transition-colors"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function ServiceAssets({ defaultSermonId }: { defaultSermonId?: string } = {}) {
  const { sermons } = useSermons();
  const {
    assets,
    loading,
    error,
    showArchived,
    setShowArchived,
    saveAsset,
    toggleArchive,
    deleteAsset,
  } = useServiceAssets();

  const [selectedSermonId, setSelectedSermonId] = useState(defaultSermonId ?? '');
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const generateSuggestions = async () => {
    if (!selectedSermonId) return;
    try {
      setGenerating(true);
      setGenerationError(null);

      const sermon = sermons.find(s => s.id === selectedSermonId);
      if (!sermon) throw new Error('Sermon not found');

      const session = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session?.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          type: 'enhance',
          content: sermon.content,
          enhancementType: 'service_assets',
        }),
      });

      if (!response.ok) throw new Error('Failed to generate suggestions');

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Failed to generate suggestions');

      let songs: string[] = [];
      let hymns: string[] = [];
      let readings: string[] = [];

      const raw = typeof result.content === 'string' ? result.content : JSON.stringify(result.content);
      const cleaned = raw.replace(/```(?:json)?/g, '').trim();
      try {
        const parsed = JSON.parse(cleaned);
        if (!parsed.songs && !parsed.hymns && !parsed.readings) {
          throw new Error('JSON missing expected keys');
        }
        songs = (parsed.songs || []).map((s: SongSuggestion) => JSON.stringify(s));
        hymns = (parsed.hymns || []).map((h: HymnSuggestion) => JSON.stringify(h));
        readings = (parsed.readings || []).map((r: ReadingSuggestion) => JSON.stringify(r));
      } catch (parseErr) {
        logger.error('Service assets JSON parse failed, raw response:', cleaned);
        throw new Error('The AI returned an unexpected format. Please try again.');
      }

      if (!songs.length && !hymns.length && !readings.length) {
        throw new Error('No suggestions were returned. Please try again.');
      }

      await saveAsset({ sermon_id: selectedSermonId, songs, hymns, readings });
      setSelectedSermonId('');
    } catch (err) {
      logger.error('Error generating service asset suggestions:', err);
      setGenerationError(err instanceof Error ? err.message : 'Failed to generate suggestions');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = (asset: typeof assets[0]) => {
    const sermon = sermons.find(s => s.id === asset.sermon_id);
    if (!sermon) return;

    const doc = new jsPDF();
    let yPos = createStyledPDF({
      title: 'Service Assets',
      subtitle: sermon.title,
      date: sermon.date ? parseISO(sermon.date).toLocaleDateString() : undefined,
    }, doc);

    if (asset.songs?.length) {
      yPos = addSection(doc, 'Suggested Songs', cleanAIArray(asset.songs.map(s => parseSong(s).title)), yPos);
    }
    if (asset.hymns?.length) {
      yPos = addSection(doc, 'Suggested Hymns', cleanAIArray(asset.hymns.map(h => parseHymn(h).title)), yPos);
    }
    if (asset.readings?.length) {
      yPos = addSection(doc, 'Scripture Readings', cleanAIArray(asset.readings.map(r => parseReading(r).reference)), yPos);
    }

    doc.save(`service-assets-${sermon.title.toLowerCase().replace(/\s+/g, '-')}.pdf`);
  };

  const shareViaEmail = (asset: typeof assets[0]) => {
    const sermon = sermons.find(s => s.id === asset.sermon_id);
    if (!sermon) return;

    let body = `Service Assets for "${sermon.title}"\n\n`;
    if (sermon.scripture) body += `Scripture: ${sermon.scripture}\n\n`;

    if (asset.songs?.length) {
      body += 'Suggested Songs:\n';
      asset.songs.forEach(s => { body += `• ${parseSong(s).title}\n`; });
      body += '\n';
    }
    if (asset.hymns?.length) {
      body += 'Suggested Hymns:\n';
      asset.hymns.forEach(h => { body += `• ${parseHymn(h).title}\n`; });
      body += '\n';
    }
    if (asset.readings?.length) {
      body += 'Scripture Readings:\n';
      asset.readings.forEach(r => { body += `• ${parseReading(r).reference}\n`; });
    }

    window.location.href = `mailto:?subject=${encodeURIComponent(`Service Assets for ${sermon.title}`)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-midnight">Service Assets</h2>
          <p className="text-sm text-slate/60 mt-0.5">AI-suggested songs, hymns & readings with setlist builder</p>
        </div>
        <button
          onClick={() => setShowArchived(!showArchived)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
            showArchived
              ? 'bg-slate/10 text-slate hover:bg-slate/20'
              : 'bg-sand/40 text-slate hover:bg-sand'
          }`}
        >
          {showArchived ? <RotateCcw className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          {showArchived ? 'Show Active' : 'Archived'}
        </button>
      </div>

      {/* Generate Panel */}
      <div className="bg-white rounded-xl shadow-sm border border-sand/30 p-5">
        <h3 className="text-sm font-semibold text-midnight mb-4">Generate Suggestions for a Sermon</h3>
        <div className="flex gap-3">
          <select
            value={selectedSermonId}
            onChange={e => setSelectedSermonId(e.target.value)}
            className="flex-1 px-3 py-2 border border-sand rounded-lg text-sm focus:ring-2 focus:ring-electric focus:border-transparent text-midnight bg-white"
          >
            <option value="">Choose a sermon...</option>
            {sermons.map(sermon => (
              <option key={sermon.id} value={sermon.id}>{sermon.title}</option>
            ))}
          </select>
          <button
            onClick={generateSuggestions}
            disabled={!selectedSermonId || generating}
            className="flex items-center gap-2 px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors whitespace-nowrap"
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
            ) : (
              <><Music className="h-4 w-4" /> Generate</>
            )}
          </button>
        </div>
      </div>

      {/* Error */}
      {generationError && (
        <div className="bg-crimson/10 text-crimson p-4 rounded-xl flex items-center gap-2 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {generationError}
        </div>
      )}

      {/* Asset List */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-7 w-7 animate-spin text-electric" />
        </div>
      ) : error ? (
        <div className="bg-crimson/10 text-crimson p-4 rounded-xl flex items-center gap-2 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      ) : assets.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-sand/30">
          <div className="w-14 h-14 bg-sand/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Music className="h-7 w-7 text-slate/40" />
          </div>
          <p className="text-slate font-medium">
            {showArchived ? 'No archived service assets' : 'No suggestions yet'}
          </p>
          <p className="text-sm text-slate/50 mt-1">
            {showArchived ? '' : 'Select a sermon above and generate your first set of suggestions.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {assets.map(asset => {
            const sermon = sermons.find(s => s.id === asset.sermon_id);
            return (
              <AssetCard
                key={asset.id}
                asset={asset}
                sermon={sermon}
                onDownload={() => downloadPDF(asset)}
                onShare={() => shareViaEmail(asset)}
                onArchive={() => toggleArchive(asset.id, asset.archived)}
                onDelete={() => deleteAsset(asset.id)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
